import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook for guide notifications
 * - Plays sound when new requests arrive
 * - Shows browser notifications even when tab is inactive
 * - Polls for new requests periodically
 */
export const useGuideNotifications = (currentRequests, onNewRequest) => {
  const previousRequestIdsRef = useRef(new Set());
  const audioContextRef = useRef(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const isInitialLoadRef = useRef(true);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      
      if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          setNotificationPermission(permission);
        });
      }
    }
  }, []);

  // Initialize AudioContext (must be done after user interaction)
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play notification sound (using Web Audio API for reliability)
  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = initAudio();
      
      // Resume audio context if suspended (due to browser autoplay policies)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      // Create a pleasant notification beep
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // First beep (higher pitch)
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      oscillator.type = 'sine';
      
      // Set volume envelope
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);

      // Second beep (slightly lower pitch) after short delay
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        
        oscillator2.frequency.setValueAtTime(659, audioContext.currentTime); // E5
        oscillator2.type = 'sine';
        
        gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        
        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.2);
      }, 150);

      // Third beep (original pitch) for pleasant chime
      setTimeout(() => {
        const oscillator3 = audioContext.createOscillator();
        const gainNode3 = audioContext.createGain();
        
        oscillator3.connect(gainNode3);
        gainNode3.connect(audioContext.destination);
        
        oscillator3.frequency.setValueAtTime(880, audioContext.currentTime); // A5
        oscillator3.type = 'sine';
        
        gainNode3.gain.setValueAtTime(0.25, audioContext.currentTime);
        gainNode3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        
        oscillator3.start(audioContext.currentTime);
        oscillator3.stop(audioContext.currentTime + 0.3);
      }, 350);

    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }, [initAudio]);

  // Show browser notification
  const showBrowserNotification = useCallback((request) => {
    if (!('Notification' in window)) return;
    
    if (Notification.permission === 'granted') {
      const notification = new Notification('🔔 New Tour Request!', {
        body: `${request.city_name}${request.monument_name ? ` - ${request.monument_name}` : ''}\n€${request.guide_fee_eur} • ${request.duration_hours}h`,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `request-${request.id}`,
        requireInteraction: true, // Stay visible until user interacts
        vibrate: [200, 100, 200], // Vibration pattern for mobile
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (onNewRequest) onNewRequest(request);
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    }
  }, [onNewRequest]);

  // Check for new requests and trigger notifications
  useEffect(() => {
    if (!currentRequests || currentRequests.length === 0) {
      // Reset initial load flag when requests is empty
      if (previousRequestIdsRef.current.size === 0) {
        isInitialLoadRef.current = true;
      }
      return;
    }

    const currentIds = new Set(currentRequests.map(r => r.id));
    
    // Skip notification on initial load
    if (isInitialLoadRef.current) {
      previousRequestIdsRef.current = currentIds;
      isInitialLoadRef.current = false;
      return;
    }

    // Find new requests (IDs that weren't in previous set)
    const newRequests = currentRequests.filter(
      request => !previousRequestIdsRef.current.has(request.id) && request.status === 'pending'
    );

    if (newRequests.length > 0) {
      console.log(`🔔 ${newRequests.length} new request(s) detected!`);
      
      // Play sound
      playNotificationSound();
      
      // Show browser notification for each new request
      newRequests.forEach(request => {
        showBrowserNotification(request);
      });
    }

    // Update previous IDs
    previousRequestIdsRef.current = currentIds;
  }, [currentRequests, playNotificationSound, showBrowserNotification]);

  // Enable audio on first user interaction (required by browsers)
  const enableAudio = useCallback(() => {
    initAudio();
  }, [initAudio]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    }
    return Notification.permission;
  }, []);

  // Test notification sound
  const testSound = useCallback(() => {
    playNotificationSound();
  }, [playNotificationSound]);

  return {
    notificationPermission,
    requestPermission,
    enableAudio,
    testSound,
    playNotificationSound,
    showBrowserNotification,
  };
};

export default useGuideNotifications;
